import React, { Fragment } from 'react';
import CustomCell from 'chameleon/ui-stack/ui/assessments/CustomCell';
import { assignmentDueDate, assignmentStartDate } from './assignmentDates';
import AssignmentSection from 'legacy-src/components/assessment/Assessments/AssignmentSection';
import AssignmentStatus from './assignment-status/AssignmentStatus';
import VideoStatus from './video-status/VideoStatus';
import styles from './styles.css';
import AssessmentsTableRow from 'chameleon/ui-stack/ui/assessments/AssessmentsTableRow';
import TextWithTooltip from 'chameleon/ui-stack/ui/assessments/text-with-tooltip';
import Results from 'chameleon/ui-stack/ui/assessments/assignment-results';
import Actions from 'chameleon/ui-stack/ui/assessments/actions';
import VideoProgressBarPopover from './video-progress-bar/VideoProgressBarPopover';
import SPProgressBarPopover from './spp-progress-bar';
import cn from 'classnames';
import { get, omit } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import buildSubgroupStudents from 'chameleon/ui-stack/utilities/utils/buildSubgroupStudents';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import axios from 'axios';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';
import { DatePickerPopup } from './date-picker-popup';
import { toast } from 'chameleon/ui-stack/ui/toast';
import { updateResourceSettings } from 'chameleon-src/queries';
import { sendToTelemetry, TLTRY_DEFAULTS } from 'legacy-src/utility/telemetry';
import isEqual from 'lodash/isEqual';
import { learnosityReportByUsers } from 'legacy-src/utility/progressHelpers';
import AssessmentName from './assessment-row/AssessmentName';
import AssessmentRow from './assessment-row/AssessmentRow';
import { SummaryRow } from './assessment-row/SummaryRow';
import { noDataPlaceholder } from './util';
import LabelList from './label-list';
import { getAssessmentLabels } from './assessment-row/util';
import usePerformanceTiers from 'chameleon/src/hooks/usePerformanceTiers';

const computeIsComplete = (progress, assignment, status, totalSubmissions) => {
  const scoredSubmissions =
    progress.complete +
    progress.scored +
    progress.teacher_and_student_scored +
    progress.student_scored;
  return status === 'closed' || scoredSubmissions === totalSubmissions;
};

const RenderRow = ({
  row,
  router,
  location,
  basePathname,
  TableColumns,
  openVideo,
  mappedGroups,
  hasStudentInClasses,
  studentsBySection,
  loadAssignments,
  subjectInfo,
  query,
  client,
  showStartDueDateDatepicker,
  onPreviewQuiz,
  me,
  closeQuizReturnPath,
  shouldStoreActionTelementryEvent = false,
  telemetryEventApp = 'assessment/unassigned',
  unassignedDateAccessor = 'date_updated',
  previewBaseUrl,
  cachedDates,
  setCachedDates,
  sharedAssessmentsFF,
  sharedQuizIconsFF,
  isAllResourcesPage,
  isAssignedResourcesPage,
  addColumnPadding,
  moveItem = () => {},
  isLast = false,
  showReorderActions = false,
  onLabelRemoved = () => {},
  videoResourcesByResourceId,
  myAssignmentsPdfFF,
  labelsDnDFF,
  onResourceLabelUpdate,
}) => {
  const { params } = router || {};
  const { id: subjectInitId } = subjectInfo || {};
  const { subject: subjectId } = params || {};

  const { performanceTiers } = usePerformanceTiers(subjectId);

  const assessment = row.original || {};
  const {
    type,
    is_assigned: isAssigned,
    showAssessmentName = false,
    title: assessmentTitle,
    id: assessmentId,
    is_spp: isSPP,
    isAssessmentRow,
    shareKey,
    labels,
  } = assessment || {};
  const isVideo = type === 'video';
  const videoOpenProps = {
    router,
    location,
    videoUrl: row.original.provider_video_id,
  };
  const assignments = assessment.assignments || [];
  const titleAriaLabel = `${assessmentTitle} Preview`;
  const { assessmentLabels, updateLabelAssociations } = getAssessmentLabels(
    { id: subjectId },
    labels,
  );

  const sendTelemetryEvent = ({ eventType = 'assign_action' }) => {
    const { id: amUserId, pilot } = window.current_user || {};
    const { initId: userId } = me;
    const payload = {
      app: telemetryEventApp,
      event_type: eventType,
      params: {},
      user: {
        am_user_id: amUserId,
        pilot,
        cb_user_id: userId,
      },
      context: {
        subject_id: subjectId,
      },
      timestamp: dayjs(),
    };
    if (shouldStoreActionTelementryEvent) {
      sendToTelemetry(Object.assign(payload, TLTRY_DEFAULTS[0]));
    }
  };

  const openPreview = (extraState = {}) => {
    // we always open the assesment preview
    // even for assignments

    if (isSPP) {
      router.push({
        pathname: `/${subjectInitId}/assessments/spp/preview/${assessmentId}`,
        state: {
          returnPath: `${location.pathname}${location.search}`,
          shareKey,
        },
        query: {
          ...query,
          editMode: 'false',
        },
      });
    } else if (onPreviewQuiz) {
      onPreviewQuiz(assessment.id);
    } else {
      router.push({
        pathname: location.pathname,
        query: {
          ...query,
          quizId: assessment.id,
          redirectUrl: '',
          editMode: 'false',
        },
        state: {
          ...extraState,
        },
      });
    }
    sendTelemetryEvent({ eventType: 'preview_action' });
  };

  const showSharedQuizIcon = () => {
    const { has_teacher_created_questions, is_from_share } = assessment;
    if (is_from_share && has_teacher_created_questions) {
      return (
        <TextWithTooltip
          ariaLabel="Created from a shared quiz and includes teacher-authored questions icon"
          tooltip="Created from a shared quiz and includes teacher-authored questions"
        >
          <Icon
            name="created_from_share_with_teacher_questions"
            height={36}
            width={36}
          />
        </TextWithTooltip>
      );
    }
    if (is_from_share) {
      return (
        <TextWithTooltip
          ariaLabel="Created from a shared quiz icon"
          tooltip="Created from a shared quiz"
        >
          <Icon name="created_from_share" height={36} width={36} />
        </TextWithTooltip>
      );
    }
    if (has_teacher_created_questions) {
      return (
        <TextWithTooltip
          ariaLabel="Includes teacher-authored questions icon"
          tooltip="Includes teacher-authored questions"
        >
          <Icon name="has_teacher_questions" height={36} width={36} />
        </TextWithTooltip>
      );
    }
  };

  const cacheUpdatedAssignment = (assignmentId, starts_at, due_at) => {
    setCachedDates({ ...cachedDates, [assignmentId]: { starts_at, due_at } });
  };

  const processDateToDisplay = (date) => {
    if (!date) {
      return date;
    }

    if (typeof date === 'string') {
      return date;
    }

    return date.format();
  };

  const updateVideoTimes = async (
    assignmentId,
    updatedFields,
    timezone,
    resolve,
    reject,
  ) => {
    const response = await client.mutate({
      mutation: updateResourceSettings,
      variables: {
        sharedResourceToClassId: assignmentId,
        startsAt: processDateToDisplay(updatedFields.starts_at),
        dueAt: processDateToDisplay(updatedFields.due_at),
        fieldChanged: updatedFields.fieldChanged,
      },
    });

    const { data: responseData } = response || {};
    const { updateResourceSettings: updateResourceSettingsData } =
      responseData || {};

    const {
      ok,
      errors,
      startsAt: serverStartsAt,
      dueAt: serverDueAt,
    } = updateResourceSettingsData || {};

    if (ok) {
      toast('Video updated successfully', { timeout: 5 });
      cacheUpdatedAssignment(assignmentId, serverStartsAt, serverDueAt);
      resolve(null);
    } else {
      let errorString = [];
      errors.forEach((error) => {
        errorString.push(error.fieldErrors.join('. '));
      });
      reject(errorString);
    }
  };

  const onAssignmentDatesChange = (assignmentId, updatedFields, timezone) => {
    return new Promise((resolve, reject) => {
      if (isVideo) {
        updateVideoTimes(
          assignmentId,
          updatedFields,
          timezone,
          resolve,
          reject,
        );
      } else {
        axios
          .put(
            `${FYM_BACKEND_URL}/assessments/api/assignments/${assignmentId}/`,
            {
              starts_at: processDateToDisplay(updatedFields.starts_at),
              due_at: processDateToDisplay(updatedFields.due_at),
              field_changed: updatedFields.fieldChanged,
            },
          )
          .then((response) => {
            const { errors, starts_at, due_at } = response.data;

            if (errors) {
              const { startsAt, dueAt } = errors;

              reject((startsAt && startsAt[0]) || (dueAt && dueAt[0]));
              return;
            }

            toast('Assignment updated successfully', { timeout: 5 });
            cacheUpdatedAssignment(assignmentId, starts_at, due_at);
            resolve(null);
          });
      }
    });
  };

  const getResultBar = (assignment, isComplete) => {
    if (isVideo) {
      return (
        <VideoProgressBarPopover
          progress={assignment.progress}
          isUnassigned={!isAssigned}
        />
      );
    }

    if (isSPP) {
      return (
        <SPProgressBarPopover
          students={get(studentsBySection, assignment.section_id, [])}
          isUnassigned={!isAssigned}
          assignment={assignment || {}}
          fromTeacherAssignmentsTable={true}
          size="wide"
        />
      );
    }

    return (
      <Results
        id={assignment.id}
        result={assignment || {}}
        groups={mappedGroups}
        isComplete={isComplete}
        isVideo={isVideo}
        performanceTiers={performanceTiers}
        studentsBySection={studentsBySection}
      />
    );
  };

  return assignments.length && !isAllResourcesPage ? (
    <Fragment>
      {isAssessmentRow && (
        <AssessmentRow
          row={row}
          subject={subjectInfo}
          onLabelRemoved={onLabelRemoved}
          labelsDnDFF={labelsDnDFF}
          assessmentName={
            <AssessmentName
              index={row.index}
              isLast={isLast}
              showReorderActions={showReorderActions}
              assessment={assessment}
              openVideo={openVideo}
              videoOpenProps={videoOpenProps}
              openPreview={openPreview}
              showSharedQuizIcon={sharedQuizIconsFF && showSharedQuizIcon()}
              moveItem={moveItem}
              titleAriaLabel={titleAriaLabel}
            />
          }
        />
      )}
      {assignments.map((assignment, index) => {
        const {
          report,
          progress,
          students_who_submitted_spp: submittedStudents,
          studentCount,
          status,
        } = assignment || {};
        const {
          submitted,
          complete,
          scored,
          student_scored,
          teacher_and_student_scored,
          mcq_scoring,
          total_students,
          watched,
        } = progress || {};
        let totalSubmissions =
          assignment &&
          Object.values(progress).reduce((curr, next) => curr + next, 0);
        let submittedSum =
          mcq_scoring +
          submitted +
          complete +
          scored +
          student_scored +
          teacher_and_student_scored;
        let isComplete = computeIsComplete(
          assignment.progress,
          assignment,
          status,
          totalSubmissions,
        );
        if (isVideo) {
          submittedSum = watched;
          totalSubmissions = total_students;
          isComplete = total_students > 0 && total_students === watched;
        } else if (isSPP) {
          const hasScoredResult = report && !isEmpty(report);
          const reportByUsers =
            hasScoredResult && learnosityReportByUsers([report]);
          const reportUsers = !isEmpty(reportByUsers)
            ? Object.keys(reportByUsers)
            : [];
          const apcLRNSubmittedIntersection = submittedStudents.filter(
            (value) => reportUsers.includes(String(value)),
          );
          submittedSum = apcLRNSubmittedIntersection.length;
          totalSubmissions = studentCount;
          isComplete = status === 'closed' || submittedSum === totalSubmissions;
        }

        const hasStudents = isVideo
          ? true
          : hasStudentInClasses(assignment.section_id);
        const assignmentProgressURL = `${basePathname}/progress/${assignment.id}`;

        const cachedStartsAt =
          cachedDates?.[assignment.id] &&
          cachedDates?.[assignment.id].starts_at;
        const cachedDueAt =
          cachedDates?.[assignment.id] && cachedDates?.[assignment.id].due_at;

        const isAutoAssigned =
          assignment.starts_at === null &&
          assignment.due_at === null &&
          assignment.actions.includes('unlock') &&
          assignment.type === 'ppc';
        const hideDatePicker =
          !showStartDueDateDatepicker ||
          assignment.print_toggle ||
          isAutoAssigned;
        const startDateIsInPast = dayjs(cachedStartsAt)
          .utc(false)
          .tz(assignment.timezone)
          .isBefore(dayjs().utc(false).tz(assignment.timezone), 'date');
        const cells = [
          CustomCell(
            `${row.id}-${2}`,
            showAssessmentName ? (
              <AssessmentName
                index={row.index}
                isLast={isLast}
                showReorderActions={showReorderActions}
                assessment={assessment}
                openVideo={openVideo}
                videoOpenProps={videoOpenProps}
                openPreview={openPreview}
                showSharedQuizIcon={sharedQuizIconsFF && showSharedQuizIcon()}
                moveItem={moveItem}
                titleAriaLabel={titleAriaLabel}
                assignment={assignment}
                studentsBySection={studentsBySection}
              />
            ) : (
              <AssignmentSection
                sectionName={assignment.section_name}
                subGroupStudents={buildSubgroupStudents(
                  assignment.sub_group_students,
                  studentsBySection,
                )}
                noDataPlaceholder={noDataPlaceholder}
                router={router}
                assignmentProgressURL={assignmentProgressURL}
                assignment={assignment}
                studentsBySection={studentsBySection}
                isSPP={assessment.is_spp}
              />
            ),
            `${styles.table_cell} ${styles.title}`,
          ),
          CustomCell(
            `labels-${row.id}`,
            // when there is only one assignment acting like an assessment row
            !isAssessmentRow && assignments.length === 1 && (
              <LabelList
                labels={assessmentLabels}
                type={type}
                assessmentId={assessmentId}
                assessmentTitle={assessmentTitle}
                updateLabelAssociations={updateLabelAssociations}
                onLabelRemoved={onLabelRemoved}
              />
            ),
            styles.light_cell,
          ),
          CustomCell(
            `${row.id}-${3}`,
            hideDatePicker ? (
              assignmentStartDate(assignment)
            ) : (
              <DatePickerPopup
                showReset={!isVideo && !!cachedStartsAt}
                timezone={assignment.timezone}
                onChange={(starts_at) =>
                  onAssignmentDatesChange(
                    assignment.id,
                    {
                      starts_at,
                      due_at: cachedDueAt,
                      fieldChanged: 'starts_at',
                    },
                    assignment.timezone,
                  )
                }
                initialValue={cachedStartsAt}
                minDate={dayjs().utc(false).tz(assignment.timezone)}
                maxDate={
                  cachedDueAt
                    ? dayjs(cachedDueAt)
                    : dayjs().utc(false).tz(assignment.timezone).add(1, 'year')
                }
              />
            ),
            `${styles.light_cell} ${styles.table_cell}`,
          ),
          CustomCell(
            `${row.id}-${4}`,
            hideDatePicker ? (
              assignmentDueDate(assignment)
            ) : (
              <DatePickerPopup
                disabled={!cachedStartsAt}
                timezone={assignment.timezone}
                onChange={(due_at) =>
                  onAssignmentDatesChange(
                    assignment.id,
                    {
                      starts_at: cachedStartsAt,
                      due_at,
                      fieldChanged: 'due_at',
                    },
                    assignment.timezone,
                  )
                }
                initialValue={cachedDueAt}
                minDate={
                  startDateIsInPast
                    ? dayjs().utc(false).tz(assignment.timezone)
                    : dayjs(cachedStartsAt)
                        .utc(false)
                        .tz(assignment.timezone)
                        .subtract(1, 'day')
                }
                showReset={!!cachedDueAt}
              />
            ),
            `${styles.light_cell} ${styles.table_cell}`,
          ),
          CustomCell(
            `${row.id}-${5}`,
            isVideo ? (
              <VideoStatus
                assignment={assignment}
                submitted={submittedSum}
                isComplete={isComplete}
                totalSubmissions={totalSubmissions}
                isUnassigned={!isAssigned}
              />
            ) : (
              <AssignmentStatus
                id={assignment.id}
                status={assignment.status}
                progress={assignment.progress}
                hasStudents={hasStudents}
                assignmentId={assignment.id}
                assignmentURL={assignmentProgressURL}
                assignmentStartDate={cachedStartsAt}
                assignmentEndDate={cachedDueAt}
                timezone={assignment.timezone}
                submitted={submittedSum}
                isComplete={isComplete}
                totalSubmissions={totalSubmissions}
                isSPP={isSPP}
                isPrint={assignment.print_toggle}
                hasAllowedLateSubmission={assignment.allow_late_submission}
              />
            ),
          ),
          CustomCell(`${row.id}-${6}`, getResultBar(assignment, isComplete)),
          CustomCell(
            `${row.id}-${7}`,
            assignment && (
              <Actions
                assignment={
                  {
                    ...assignment,
                    assessment: { ...omit(assessment, ['assignments']) },
                  } || {}
                }
                type={assignment.type}
                basePathname={basePathname}
                fetchAssessmentData={loadAssignments}
                subjectInitId={subjectInitId}
                assessment={assessment}
                subjectInfo={subjectInfo}
                isFinalExam={assignment.isFinalExam}
                isPilot={assignment.isPilot}
                isSPP={assessment.is_spp}
                hasStudents={hasStudents}
                section={assignment.section}
                isVideo={isVideo}
                startsAt={cachedStartsAt}
                dueAt={cachedDueAt}
                shareAssessmentInviteItemId={
                  !sharedAssessmentsFF ? null : assessment.id
                }
                shareKey={row.original.shareKey}
                isMyAssignmentsPage
                videoResourcesByResourceId={videoResourcesByResourceId}
                onResourceLabelUpdate={onResourceLabelUpdate}
                assignmentURL={assignmentProgressURL}
                isAssignedResourcesPage={isAssignedResourcesPage}
              />
            ),
          ),
        ];

        return (
          <AssessmentsTableRow
            key={`${row.id}-${assignment.id}`}
            row={row}
            rowId={`${row.original.id}/${assignment.id}`}
            labelsDnDFF={labelsDnDFF}
            className={cn({
              'border-gray-400': index === assignments.length - 1,
              'border-b-0': index !== assignments.length - 1,
            })}
            isDisabled={isVideo}
            onClick={(e) => {
              e.stopPropagation();
              // this is wrong, the only element which matches is the date/time.
              const isFromTableRow = e.target.parentNode.classList.contains(
                'assignments-table-row',
              );

              // element has data-row-click="true" attribute
              const isClickableRowArea = e.target.dataset.rowClick;

              (isFromTableRow || isClickableRowArea) &&
                !isVideo &&
                router.push(assignmentProgressURL);
            }}
            aria-label={assessmentTitle}
            isSingleAssignmentRow={!isAssessmentRow && assignments.length === 1}
          >
            <TableColumns
              cells={cells}
              className={
                showAssessmentName
                  ? styles.table_columns_chunky
                  : styles.table_columns
              }
            />
          </AssessmentsTableRow>
        );
      })}
    </Fragment>
  ) : (
    <SummaryRow
      key={row.id}
      row={row}
      sharedQuizIcon={sharedQuizIconsFF && showSharedQuizIcon()}
      assessment={assessment}
      titleAriaLabel={titleAriaLabel}
      onClick={(e) => {
        e.stopPropagation();
        isVideo ? openVideo(videoOpenProps) : openPreview();
      }}
      unassignedDateAccessor={unassignedDateAccessor}
      isVideo={isVideo}
      basePathname={basePathname}
      fetchAssessmentData={loadAssignments}
      subjectInitId={subjectInitId}
      subjectInfo={subjectInfo}
      sendTelemetryEvent={sendTelemetryEvent}
      closeQuizReturnPath={closeQuizReturnPath}
      previewBaseUrl={previewBaseUrl}
      sharedAssessmentsFF={sharedAssessmentsFF}
      addColumnPadding={addColumnPadding}
      showReorderActions={showReorderActions}
      moveItem={moveItem}
      onLabelRemoved={onLabelRemoved}
      isLast={isLast}
      studentsBySection={studentsBySection}
      learnosityReportByUsers={learnosityReportByUsers}
      videoResourcesByResourceId={videoResourcesByResourceId}
      myAssignmentsPdfFF={myAssignmentsPdfFF}
      labelsDnDFF={labelsDnDFF}
      onResourceLabelUpdate={onResourceLabelUpdate}
    />
  );
};

/**
 * Only the changing of dates should cause this component update
 */

const equalityCheck = (prevProps, newProps) => {
  const { row } = newProps;
  const { original: assessment } = row || {};
  const { assignments } = assessment || {};

  if (assignments.length === 0) {
    return true;
  }

  const { row: oldRow } = prevProps;
  const { original: oldAssessment } = oldRow || {};
  const { assignments: oldAssignments } = oldAssessment || {};

  const oldResults = (oldAssignments || []).reduce(
    (curr, next) => ({ ...curr, [curr[next.id]]: next.report }),
    {},
  );

  const newResults = (assignments || []).reduce(
    (curr, next) => ({ ...curr, [curr[next.id]]: next.report }),
    {},
  );

  const { cachedDates: newCachedDates } = newProps;
  const { cachedDates: oldCachedDates } = prevProps;

  const changes = assignments.filter(
    ({ id }) =>
      !isEqual(oldCachedDates[id], newCachedDates[id]) ||
      !isEqual(oldResults[id], newResults[id]),
  );

  return changes.length === 0;
};

const RenderRowNoRerender = React.memo(RenderRow, equalityCheck);

export default RenderRowNoRerender;
